// Tracking Health — dispatch_admin's parcel-intervention board. Which
// tracking numbers India Post doesn't recognise, which parcels stopped
// moving, what's stuck or returned, and what's ageing with Ops. Every
// number opens its order list (shared drill modal) and exports CSV.

import { useEffect, useState } from 'react';
import { trackingHealthApi } from '../api/services';
import InfoTip from '../components/InfoTip';
import DrillModal, { useDrill } from '../components/DrillModal';
import toast from 'react-hot-toast';
import { Loader2, Radar, RefreshCcw } from 'lucide-react';

function Tile({ label, value, info, alert, warn, onClick }) {
  const tone = alert ? 'border-rose-300 bg-rose-50/60'
    : warn ? 'border-amber-300 bg-amber-50/60'
    : 'border-emerald-200 bg-emerald-50/40';
  return (
    <div className={`rounded-xl border p-4 ${tone}`}>
      <div className="flex items-center justify-between gap-1">
        <button onClick={onClick} title="See which orders these are"
                className="text-[11px] uppercase tracking-wide text-blue-600 hover:underline text-left">
          {label}
        </button>
        <InfoTip title={label}>{info}</InfoTip>
      </div>
      <button onClick={onClick} title="See which orders these are"
              className={`text-3xl font-semibold tabular-nums hover:underline ${alert ? 'text-rose-700' : warn ? 'text-amber-700' : 'text-gray-900'}`}>
        {value}
      </button>
    </div>
  );
}

function BucketCell({ count, tone, onClick }) {
  if (count === 0) return <td className="px-3 py-2 text-right text-gray-300">—</td>;
  const tones = {
    ok: 'text-gray-700',
    warn: 'text-amber-700 font-medium',
    alarm: 'text-rose-700 font-semibold',
  };
  return (
    <td className="px-3 py-2 text-right">
      <button onClick={onClick} title="See which orders these are"
              className={`tabular-nums hover:underline ${tones[tone]}`}>
        {count}
      </button>
    </td>
  );
}

export default function TrackingHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { drill, drillData, drillLoading, openDrill, exportDrill, closeDrill } = useDrill();

  function load() {
    setLoading(true);
    trackingHealthApi.build()
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Radar className="w-5 h-5 text-indigo-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Tracking Health</h1>
          <InfoTip title="What this board is for" align="left">
            Which parcels need somebody to intervene. <b>Not recognised</b> =
            India Post returns nothing for the tracking number (typo, or
            never handed to the post office — check with the vendor).{' '}
            <b>No movement</b> = the parcel exists but stopped moving — catch
            it before the customer asks. <b>Returned</b> parcels need a
            reship-or-refund decision. <b>With Ops &gt; 5 days</b> is the
            Ops backlog worth a nudge. Every number opens the exact parcels;
            every list exports to CSV.
          </InfoTip>
        </div>
        <button onClick={load} disabled={loading}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Parcels that need a human. Empty is good.
      </p>

      {loading && !data && (
        <div className="py-16 text-center text-sm text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Checking…
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
            <Tile label="TN not recognised"
                  value={data.notConnected.total}
                  alert={data.notConnected.total > 0}
                  onClick={() => openDrill('tn_not_connected')}
                  info="Tracking numbers India Post has never returned data for. After 2-3 days this almost always means a typo in the number or a parcel that was never actually posted — verify the number with the vendor, then correct it on the order." />
            <Tile label="No movement 7+ days"
                  value={data.noMovement7d.total}
                  warn={data.noMovement7d.total > 0}
                  onClick={() => openDrill('no_movement_7d')}
                  info="Parcels India Post knows about but whose tracking hasn't changed in over a week. These become 'stuck' and then customer complaints — flag them to India Post early. Days count from the last actual tracking event." />
            <Tile label="Stuck in transit > 14d"
                  value={data.stuckGt14.total}
                  alert={data.stuckGt14.total > 0}
                  onClick={() => openDrill('stuck_in_transit_gt_14d')}
                  info="The same rule the owners see on their cockpit: parcels in transit over 14 days from when the tracking number was recorded. These need an India Post complaint reference — customers will be asking." />
            <Tile label="Returned — decide"
                  value={data.returnedNeedsAction.total}
                  alert={data.returnedNeedsAction.total > 0}
                  onClick={() => openDrill('returned_needs_action')}
                  info="India Post sent the parcel back and the order is still open — each one needs a decision: reship it or refund the customer. The longer it sits, the worse the customer experience." />
            <Tile label="With Ops > 5 days"
                  value={data.opsHoldingOver5d.total}
                  warn={data.opsHoldingOver5d.total > 0}
                  onClick={() => openDrill('stuck_over_5d', { team: 'ops' })}
                  info="Orders that have been sitting in the Ops queue for more than 5 days (e.g. partial-shipment notifications not yet sent to the customer). Export the list and nudge Ops — the owners see this same number on their cockpit." />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
              Unrecognised tracking numbers — by vendor and age{' '}
              <InfoTip title="Reading this table">
                Each vendor's unrecognised tracking numbers, bucketed by how
                long ago we recorded them. <b>0-2d</b> can still be India Post
                lag; <b>3-5d</b> verify with the vendor; <b>6+d</b> is almost
                certainly a wrong number or an unposted parcel. Click any
                count for the exact orders and tracking numbers — export and
                send to the vendor to verify in one go.
              </InfoTip>
            </div>
            {data.notConnected.byVendor.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                Every tracking number is recognised by India Post. Clean.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Vendor</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-right px-3 py-2">0-2d</th>
                    <th className="text-right px-3 py-2">3-5d</th>
                    <th className="text-right px-3 py-2">6+d</th>
                    <th className="text-right px-3 py-2">Oldest</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.notConnected.byVendor.map((v) => (
                    <tr key={v.vendor_id || v.vendor_name}>
                      <td className="px-3 py-2 font-medium">{v.vendor_name || '(unknown vendor)'}</td>
                      <BucketCell count={v.total} tone="warn"
                                  onClick={() => openDrill('tn_not_connected', { vendorId: v.vendor_id })} />
                      <BucketCell count={v.b0_2} tone="ok"
                                  onClick={() => openDrill('tn_not_connected', { vendorId: v.vendor_id, bucket: 'b0_2' })} />
                      <BucketCell count={v.b3_5} tone="warn"
                                  onClick={() => openDrill('tn_not_connected', { vendorId: v.vendor_id, bucket: 'b3_5' })} />
                      <BucketCell count={v.b6p} tone="alarm"
                                  onClick={() => openDrill('tn_not_connected', { vendorId: v.vendor_id, bucket: 'b6p' })} />
                      <td className={`px-3 py-2 text-right text-xs tabular-nums ${v.oldest_days > 5 ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
                        {v.oldest_days != null ? `${v.oldest_days}d` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {drill && (
        <DrillModal data={drillData} loading={drillLoading}
                    onClose={closeDrill} onExport={exportDrill} />
      )}
    </div>
  );
}
